import { Grid, GridColumn, GridRow, Tab } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDeputateLessons } from '../../../../components/Lessons/hooks/useDeputateLessons';
import { DeputateLessonsTable } from '../../../../components/Lessons/Tables/DeputateLessonsTable';
import { LessonStatsTable } from '../../../../components/Lessons/Tables/LessonStatsTable';
import { LessonTeacherInfoTable } from '../../../../components/Lessons/Tables/LessonTeacherInfoTable';
import { Page } from '../../../../components/Page/Page';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../../../hooks/useUserConfigContext';
import { hexToColorOption } from '../../../../utils/colorUtils';
import { LessonTabsType } from './LessonsPage';

export const LessonSubjectDetailSubpage = observer(() => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useUserConfigContext().selectedSchoolYear;
  const { subjectUuid } = useParams();
  const permissions = usePermissionChecker();

  const { lessonsData } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const filteredLessons = lessonsData.filter((lesson) => {
    return lesson.subjectUuid === subjectUuid;
  });

  const assignedData = filteredLessons.filter((l) => l.teachers.length > 0);
  const unassignedData = filteredLessons.filter((l) => l.teachers.length === 0);

  const { subjectData, teacherData } = useLoadBasicData({ pause: !schoolYear });

  const currentSubject = subjectData?.subjects.find((s) => s.uuid === subjectUuid);

  const { html, label } = hexToColorOption(currentSubject?.timetableConfig?.color ?? '');

  const defaultInitialValues = {
    teachingLoadEnabled: true,
    subject: {
      uuid: currentSubject?.uuid ?? '',
      name: currentSubject?.name ?? '',
    },
  };

  const tabs: LessonTabsType[] = [
    {
      title: t('lesson.all'),
      value: 'all',
      content: (
        <DeputateLessonsTable
          lessonTabFilterType='all'
          configKey='lessons-table-subjects-all'
          lessonsData={filteredLessons}
          defaultInitialValues={defaultInitialValues}
        />
      ),
    },
    {
      title: t('lesson.assigned'),
      value: 'assigned',
      content: (
        <DeputateLessonsTable
          lessonTabFilterType='assigned'
          configKey='lessons-table-subjects-assigned'
          lessonsData={assignedData ?? []}
          defaultInitialValues={defaultInitialValues}
        />
      ),
    },
    {
      title: t('lesson.unassigned'),
      value: 'unassigned',
      content: (
        <DeputateLessonsTable
          lessonTabFilterType='unassigned'
          configKey='lessons-table-subjects-unassigned'
          lessonsData={unassignedData ?? []}
          defaultInitialValues={defaultInitialValues}
        />
      ),
    },
  ];

  return (
    <Page
      isForbidden={!permissions?.canReadDeputate()}
      title={currentSubject?.name}
      colorHint={{ color: html, colorLabel: label }}
    >
      <Grid>
        <GridRow>
          <GridColumn width={4}>
            <LessonStatsTable lessonsData={filteredLessons} />
          </GridColumn>
          <GridColumn width={8}>
            <LessonTeacherInfoTable
              teachers={teacherData?.people ?? []}
              lessonsData={filteredLessons}
              subjectUuid={subjectUuid ?? ''}
              subjectName={currentSubject?.name}
            />
          </GridColumn>
        </GridRow>
        <GridRow>
          <div style={{ display: 'block', width: '100%' }}>
            <Tab tabs={tabs} />
          </div>
        </GridRow>
      </Grid>
    </Page>
  );
});
